//client\src\coms\views\watch_together_parts\WatchTogetherQueuePanel.js
import React from 'react';
import { getProviderLabel, getProviderPlaceholder } from './watchTogetherUtils';

function detectProviderFromInput(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  if (
    text.includes('open.spotify.com/') ||
    text.startsWith('spotify:') ||
    text.includes('spotify.link/')
  ) {
    return 'spotify';
  }
  if (
    text.includes('youtube.com/watch') ||
    text.includes('youtube.com/shorts/') ||
    text.includes('youtube.com/live/') ||
    text.includes('youtu.be/')
  ) {
    return 'youtube';
  }
  if (text.startsWith('http://') || text.startsWith('https://')) {
    return 'misc';
  }
  return '';
}

export default function WatchTogetherQueuePanel({
  provider,
  setProvider,
  providers,
  input,
  setInput,
  normalizedDraft,
  shareReady,
  addToQueue,
  clearQueue,
  sharedQueue,
  activeQueueId,
  activeSkipVotes,
  majorityNeeded,
  makeActive,
  removeFromQueue,
  canControlPlayback = true,
  canAddToQueue = canControlPlayback,
  canManageQueue = canControlPlayback,
  canRemoveOwnItems = canControlPlayback,
  canChooseActive = canControlPlayback,
  identity = '',
}) {
  // What this member may do (roles, channel overrides, host-only mode). Receivers ignore anything else.
  const canAdd = shareReady && canAddToQueue;
  const canClear = shareReady && canManageQueue;
  const canMakeActive = shareReady && canChooseActive;
  const canRemove = (item) => shareReady && (canManageQueue || (canRemoveOwnItems && String((item && item.addedByIdentity) || '') === String(identity || '')));
  const detectedProvider = detectProviderFromInput(input);
  const effectiveProvider = detectedProvider || provider;
  const effectiveProviderLabel = getProviderLabel(effectiveProvider);

  const placeholder = detectedProvider
    ? getProviderPlaceholder(detectedProvider)
    : 'Paste a YouTube, Spotify, or misc media link';

  const handleInputChange = (e) => {
    const nextValue = e.target.value;
    setInput(nextValue);

    const detected = detectProviderFromInput(nextValue);
    if (detected && detected !== provider) {
      setProvider(detected);
    }
  };

  return (
    <div
      style={{
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
        background: 'rgba(var(--vxs-1-rgb, 17,18,20), 0.45)',
        borderRadius: 14,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.9 }}>Add item</div>

        <div
          style={{
            height: 30,
            borderRadius: 999,
            padding: '0 10px',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            background: detectedProvider ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.18)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            color: 'var(--vx-text, #fff)',
            display: 'inline-flex',
            alignItems: 'center',
            fontWeight: 800,
            fontSize: 12,
            whiteSpace: 'nowrap',
          }}
        >
          {detectedProvider ? `Detected ${effectiveProviderLabel}` : 'Paste a link to detect provider'}
        </div>
      </div>

      <input
        value={input}
        onChange={handleInputChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          height: 42,
          borderRadius: 12,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
          color: 'var(--vx-text, #fff)',
          padding: '0 12px',
          outline: 'none',
          fontSize: 14,
          flexShrink: 0,
        }}
      />

      <div style={{ fontSize: 12, opacity: 0.72, lineHeight: 1.45, marginTop: -4 }}>
        Paste a YouTube, Spotify, Vimeo, Dailymotion, Loom, Streamable, Wistia, MP4, WebM, or audio link. Provider is auto-detected.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
        <button
          type="button"
          onClick={addToQueue}
          disabled={!normalizedDraft.ok || !canAdd}
          style={{
            height: 40,
            borderRadius: 12,
            padding: '0 14px',
            border: '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.55)',
            background: normalizedDraft.ok && canAdd ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.95)' : 'rgba(var(--vx-accent-rgb, 88,101,242), 0.32)',
            color: 'var(--vx-on-accent, #fff)',
            cursor: normalizedDraft.ok && canAdd ? 'pointer' : 'not-allowed',
            fontWeight: 900,
            fontSize: 13,
          }}
        >
          Add to queue
        </button>

        <button
          type="button"
          onClick={clearQueue}
          disabled={!sharedQueue.length || !canClear}
          style={{
            height: 40,
            borderRadius: 12,
            padding: '0 14px',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            color: 'var(--vx-text, #fff)',
            cursor: sharedQueue.length && canClear ? 'pointer' : 'not-allowed',
            fontWeight: 800,
            fontSize: 13,
            opacity: sharedQueue.length && canClear ? 1 : 0.55,
          }}
        >
          Clear queue
        </button>
      </div>

      <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.9, marginTop: 2, flexShrink: 0 }}>Shared queue</div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          flex: 1,
          paddingRight: 4,
        }}
      >
        {!sharedQueue.length ? (
          <div
            style={{
              borderRadius: 12,
              border: '1px dashed rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
              padding: 16,
              fontSize: 13,
              opacity: 0.76,
              lineHeight: 1.5,
            }}
          >
            Queue is empty. Paste a YouTube, Spotify, or misc media item above.
          </div>
        ) : null}

        {sharedQueue.map((item, index) => {
          const isActive = item.id === activeQueueId;
          const itemVoteCount = isActive ? activeSkipVotes.length : 0;
          const providerLabel = getProviderLabel(item.provider);

          return (
            <div
              key={item.id}
              style={{
                borderRadius: 14,
                padding: 12,
                background: isActive ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.18)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                border: isActive ? '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.42)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                flexShrink: 0,
              }}
            >
              <img
                src={item.thumbnailUrl}
                alt=""
                style={{
                  width: 112,
                  aspectRatio: '16 / 9',
                  objectFit: 'cover',
                  borderRadius: 10,
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                  background: 'rgba(0,0,0,0.35)',
                  flexShrink: 0,
                }}
              />

              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 900 }}>#{index + 1}</div>
                  <div
                    style={{
                      borderRadius: 999,
                      padding: '4px 8px',
                      fontSize: 11,
                      fontWeight: 900,
                      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                    }}
                  >
                    {providerLabel}
                  </div>
                  {isActive ? (
                    <div
                      style={{
                        borderRadius: 999,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 900,
                        background: 'rgba(var(--vx-accent-rgb, 99,102,241), 0.22)',
                        border: '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.42)',
                      }}
                    >
                      Active
                    </div>
                  ) : null}
                  {isActive ? (
                    <div
                      style={{
                        borderRadius: 999,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 900,
                        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                      }}
                    >
                      Skip votes {itemVoteCount}/{majorityNeeded}
                    </div>
                  ) : null}
                </div>

                <div style={{ fontSize: 13, fontWeight: 700, wordBreak: 'break-word', lineHeight: 1.45 }}>
                  {item.shareUrl}
                </div>

                <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.45 }}>
                  Added by {item.addedBy || 'Someone'}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => makeActive(item.id)}
                    disabled={!canMakeActive}
                    style={{
                      height: 34,
                      borderRadius: 10,
                      padding: '0 12px',
                      border: '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.42)',
                      background: 'rgba(var(--vx-accent-rgb, 88,101,242), 0.18)',
                      color: 'var(--vx-text, #fff)',
                      cursor: canMakeActive ? 'pointer' : 'not-allowed',
                      opacity: canMakeActive ? 1 : 0.55,
                      fontWeight: 800,
                      fontSize: 12,
                    }}
                  >
                    Make active
                  </button>

                  <button
                    type="button"
                    onClick={() => removeFromQueue(item.id)}
                    disabled={!canRemove(item)}
                    style={{
                      height: 34,
                      borderRadius: 10,
                      padding: '0 12px',
                      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                      color: 'var(--vx-text, #fff)',
                      cursor: canRemove(item) ? 'pointer' : 'not-allowed',
                      opacity: canRemove(item) ? 1 : 0.55,
                      fontWeight: 800,
                      fontSize: 12,
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}